import {useMemo} from 'react';

import {useConfigValuesAggregationQueryQuery} from '../../../../../generated/graphql';
import {NetworkStatus} from '../../../../../state/graphql/apolloWithAbort';
import * as QueryTS from '../../../../../util/queryts';
import {type Sort} from '../../../../../util/queryTypes';

export type ConfigValueForRunValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | unknown[]
  | Record<string, unknown>;

export type ConfigValueForRun = {
  runName: string;
  value: ConfigValueForRunValue;
};

type ConfigValuesByRunName = Record<string, ConfigValueForRunValue>;

export type ConfigValuesNumberAggregation = {
  min: number;
  max: number;
};

export function useConfigValuesAggregation({
  currentKey,
  entityName,
  filters,
  order,
  projectName,
  shouldSkip,
}: {
  currentKey?: string;
  entityName: string;
  filters: string;
  order: Sort;
  projectName: string;
  shouldSkip: boolean;
}): {
  configValuesForRun: ConfigValueForRun[];
  configValuesNumberAggregation: ConfigValuesNumberAggregation;
  configValuesError?: Error;
  configValuesLoading: boolean;
} {
  // Get the config values for each run
  const {
    data,
    error: configValuesError,
    networkStatus,
  } = useConfigValuesAggregationQueryQuery({
    context: {
      propagateErrors: true,
    },
    variables: {
      filters: filters,
      limit: 100,
      order: order ? QueryTS.sortToOrderString(order) : '-createdAt',
      configKey: currentKey?.replace('config.', '') ?? '', // this cannot be hit because the query will be skipped when `currentKey` doesn't exist
      configType: 'number',
      entityName,
      projectName,
    },
    notifyOnNetworkStatusChange: true,
    skip: shouldSkip,
  });
  const configValuesLoading = networkStatus !== NetworkStatus.ready;

  const {configValuesForRun, configValuesNumberAggregation} = useMemo(() => {
    try {
      const configValuesForRun: ConfigValueForRun[] = [];
      const configValues = (data?.project?.runs?.configValues ??
        {}) as ConfigValuesByRunName;
      for (const [key, value] of Object.entries(configValues)) {
        configValuesForRun.push({
          runName: key,
          value: value,
        });
      }
      const aggregation = data?.project?.runs?.configValuesNumberAggregation;
      return {
        configValuesForRun,
        configValuesNumberAggregation: aggregation
          ? {min: aggregation.Min, max: aggregation.Max}
          : {min: 0, max: 0},
      };
    } catch (e) {
      console.error(e);
      return {
        configValuesForRun: [],
        configValuesNumberAggregation: {min: 0, max: 0},
      };
    }
  }, [data]);

  return useMemo(
    () => ({
      configValuesForRun,
      configValuesNumberAggregation,
      configValuesError,
      configValuesLoading,
    }),
    [
      configValuesForRun,
      configValuesNumberAggregation,
      configValuesError,
      configValuesLoading,
    ]
  );
}
